<template>
    <v-dialog
        :model-value="show && messages.length > 0"
        :scrim="false"
        width="100%"
        max-width="800"
        persistent
        scrollable
        class="prepare-book-messages"
    >
        <v-card>
            <v-card-title>
                <div class="text-center text-warning text-h5 mb-2 full-width">
                    <v-icon size="large" color="warning">mdi-alert-outline</v-icon>
                    {{ $t('important_information') }}
                </div>
            </v-card-title>
            <v-card-subtitle>
                <div class="text-center mb-2">
                    {{ $t('vendor_message_note') }}
                </div>
                <slot name="notes" />
            </v-card-subtitle>
            <v-card-text>
                <div
                    v-for="(
                        {
                            name,
                            vendorMessages,
                            tips,
                            warningMessages,
                            errorMessages,
                            cancellations,
                            cancellationsChanged,
                            cancellationsMessagesIndex,
                            serviceType,
                            serviceDetails,
                            processId,
                            availableForBooking,
                            onRequest,
                            additionalQuestion,
                        },
                        index
                    ) in messages"
                    :key="index"
                    class="my-5"
                >
                    <span
                        v-if="prepareBookResponse.prepareBookResponses"
                        class="text-subtitle-1 font-weight-bold d-flex"
                    >
                        <service-icon v-if="serviceType" :service="{serviceType, serviceDetails}" />
                        {{ name }}
                    </span>
                    <div
                        v-for="(message, messageIndex) in errorMessages"
                        :key="messageIndex"
                        :class="{'mb-4': errorMessages?.length && warningMessages?.length}"
                        class="mb-2 text-error font-weight-bold"
                        v-text="message"
                    />
                    <div
                        v-for="(message, messageIndex) in warningMessages"
                        :key="messageIndex"
                        :class="{'mb-4': warningMessages?.length && vendorMessages?.length}"
                        class="mb-2"
                    >
                        <div class="text-warning font-weight-bold">{{ message }}</div>
                        <conditions
                            v-if="cancellationsMessagesIndex === messageIndex && cancellationsChanged"
                            :conditions-info-data="{cancellationItems: cancellations}"
                            :elevation="0"
                            hide-close-btn
                            hide-title
                        />
                    </div>
                    <div
                        v-for="(message, messageIndex) in vendorMessages"
                        :key="messageIndex"
                        class="font-weight-bold"
                        v-html="message"
                    />
                    <order-details-tips class="mb-2" :outlined="false" :tips="tips" :loaded="true" center />
                    <slot
                        :available-for-booking="availableForBooking"
                        :on-request="onRequest"
                        :service-type="serviceType"
                    />
                    <order-details-questions
                        v-if="additionalQuestion && additionalQuestion.length"
                        v-model="answersToQuestionsValid"
                        class="px-4"
                        :is-saving-without-details="isSavingWithoutDetails"
                        :additional-questions="additionalQuestion"
                        :process-id="processId"
                        @answers-changed="answersChanged"
                    />
                </div>
            </v-card-text>
            <v-divider />
            <v-card-actions class="justify-center">
                <v-btn
                    v-if="availableForBooking"
                    color="primary"
                    :disabled="!answersToQuestionsValid"
                    variant="text"
                    @click="approve"
                >
                    {{ $t('continue') }}
                </v-btn>
                <v-btn variant="text" @click="cancel">
                    {{ $t('cancel') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Vue} from 'vue-facing-decorator'
    import OrderDetailsTips from '@/components/account/OrderDetailsTips.vue'
    import OrderDetailsQuestions from '@/components/account/OrderDetailsQuestions.vue'
    import {
        prepareBookResult,
        STANDARD_ERROR_MESSAGES,
        WARNING_MESSAGE_EXTERNAL_CANCELLATIONS_HAS_BEEN_CHANGED,
        WARNING_MESSAGE_PRICE_CHANGED,
    } from '@/utils/PrepareBookAdapter'

    import Conditions from '@/components/account/Conditions.vue'
    import ServiceIcon from '@/components/account/snippets/ServiceIcon.vue'

    @Component({
        components: {ServiceIcon, OrderDetailsTips, Conditions, OrderDetailsQuestions},
    })
    export class PrepareBookMessagesModal extends Vue {
        @Prop({required: true, type: Object}) prepareBookResponse
        @Prop({required: true, type: Array}) offersData
        @Prop({default: false, type: Boolean}) showAll
        @Prop({default: false, type: Boolean}) isSavingWithoutDetails

        show = true
        answersToQuestionsValid = true

        @Emit()
        approve() {
            this.show = false
        }

        @Emit()
        cancel() {
            this.show = false
        }

        @Emit()
        answersChanged(answers) {
            return answers
        }

        changedPriceLabel(price) {
            return this.$filters.formatPrice(price)
        }

        get messages() {
            return prepareBookResult(this.prepareBookResponse, this.offersData).reduce(
                (
                    messages,
                    {
                        name,
                        vendorMessages,
                        tips,
                        warningMessages,
                        errorMessages,
                        price,
                        supplierPrice,
                        availableForBooking,
                        paymentByLoyaltyPoint,
                        cancellations,
                        cancellationsChanged,
                        cancellationsMessagesIndex,
                        serviceType,
                        serviceDetails,
                        processId,
                        status,
                        onRequest,
                        additionalQuestion,
                    }
                ) => {
                    if (
                        vendorMessages.length > 0 ||
                        tips.length > 0 ||
                        additionalQuestion?.length > 0 ||
                        warningMessages.length > 0 ||
                        errorMessages.length > 0 ||
                        !availableForBooking ||
                        this.showAll
                    ) {
                        messages.push({
                            name,
                            vendorMessages,
                            tips,
                            warningMessages: warningMessages.map(message => {
                                if (WARNING_MESSAGE_PRICE_CHANGED === message) {
                                    return this.isTO1 && supplierPrice
                                        ? this.$t(
                                              this.$route.name !== 'order-details'
                                                  ? 'warning_message.price_changed_to1'
                                                  : 'warning_message.price_changed_to1-second',
                                              {
                                                  price: this.changedPriceLabel(price, paymentByLoyaltyPoint),
                                                  supplierPrice: this.$filters.formatPrice(supplierPrice),
                                              }
                                          )
                                        : this.$t(message, {
                                              price: this.changedPriceLabel(price, paymentByLoyaltyPoint),
                                          })
                                } else if (WARNING_MESSAGE_EXTERNAL_CANCELLATIONS_HAS_BEEN_CHANGED === message) {
                                    return this.$t('warning_message.external_cancellations_has_been_changed')
                                } else {
                                    return message
                                }
                            }),
                            errorMessages: errorMessages.map(message =>
                                STANDARD_ERROR_MESSAGES.includes(message) ? this.$t(message) : message
                            ),
                            availableForBooking,
                            cancellations,
                            cancellationsChanged,
                            cancellationsMessagesIndex,
                            ...(serviceType && {serviceType}),
                            ...(serviceDetails && {serviceDetails}),
                            processId,
                            status,
                            onRequest,
                            ...(additionalQuestion && {additionalQuestion}),
                        })
                    }
                    return messages
                },
                []
            )
        }

        get availableForBooking() {
            return (
                this.messages.every(({availableForBooking}) => availableForBooking) ||
                this.messages.some(({status}) => status === 0)
            )
        }

        get isTO1() {
            return authStore.isTO1
        }
    }

    export default PrepareBookMessagesModal
</script>
